/**
 * Pure helpers for the Settings side-tab list.
 *
 * Kept free of React so the gating rule for the SaaS-only "Plans & Billing"
 * tab can be unit-tested without rendering ``Settings``.
 */

export const BILLING_TAB = { id: 'billing', label: 'Plans & Billing' };

/** Monthly token spend per operation; same gate as billing, always right after it. */
export const CREDIT_USAGE_TAB = { id: 'credit-usage', label: 'Credit Usage' };

/** God-only review queue for Creator requests filed through ``/waitlist``. */
export const WAITING_LIST_TAB = { id: 'waiting-list', label: 'Waiting List' };

/**
 * MCP client config for this install (Figma 9851:18381), right before API
 * Keys. ``Settings`` drops it when the backend reports ``mcp_enabled`` false
 * — the same gate that hides the "MCP Server" docs page and the avatar-menu
 * "Try Specific’s Remote MCP" row.
 */
export const MCP_TAB = { id: 'mcp', label: 'MCP' };

/**
 * Workspace tab (Figma 9244:4940). Always the first tab and the default
 * landing tab for every member: Admins and gods get the logo + name editor
 * (``PATCH /workspaces/{id}``, which the backend gates the same way), while
 * Editors and Viewers get a read-only summary of the workspace they are in.
 */
export const WORKSPACE_TAB = { id: 'workspace', label: 'Workspace' };

/**
 * Settings tabs the platform owns on SaaS — hardware device, external deploy
 * targets, and the personal Hugging Face / Tavily tokens. Hidden (and their
 * write routes 403) for everyone but gods when ``canManagePlatformSettings``
 * is false; see ``features/auth/providerAccess``. Model Providers has the
 * same policy under its own flag because its label carries a count.
 */
export const PLATFORM_SETTINGS_TAB_IDS = Object.freeze(['hardware', 'deploy-targets', 'hf-token', 'tavily-key']);

/** ``/settings?tab=<tab>`` plus the usecase id when one is active. */
export function settingsTabPath(tab, usecaseId) {
    const params = new URLSearchParams({ tab });
    if (usecaseId) params.set('usecaseId', usecaseId);
    return `/settings?${params.toString()}`;
}

/** Model-provider credentials tab; hidden for SaaS non-gods. */
export const PROVIDERS_TAB_ID = 'providers';

/** User Management tab — where teammates are invited. */
export const USERS_TAB_ID = 'users';

/**
 * Tabs the billing entry is inserted directly after, first match wins. The
 * design order is Workspace, Profile, User Management, Plans & Billing,
 * Model Providers, … (Figma 9244:4940), so billing sits right after User
 * Management; ``profile`` and ``providers`` are fallbacks for lists that
 * lack it.
 */
const BILLING_ANCHOR_TAB_IDS = [USERS_TAB_ID, 'profile', PROVIDERS_TAB_ID];

/** Tab the waiting-list entry is inserted directly after. */
const WAITING_LIST_ANCHOR_TAB_IDS = [USERS_TAB_ID];

/**
 * Insert ``tab`` right after the first present ``anchorIds`` entry when
 * ``show`` holds, otherwise strip any stale copy of it. Appends when no
 * anchor is present; never duplicates (an existing copy is replaced in
 * place, so a relabelled tab is refreshed).
 */
function withGatedTab(items, tab, anchorIds, show) {
    if (!show) {
        return items.filter((item) => item.id !== tab.id);
    }
    if (items.some((item) => item.id === tab.id)) {
        return items.map((item) => (item.id === tab.id ? tab : item));
    }
    const anchorIndex = anchorIds
        .map((anchorId) => items.findIndex((item) => item.id === anchorId))
        .find((index) => index !== -1) ?? -1;
    if (anchorIndex === -1) {
        return [...items, tab];
    }
    return [
        ...items.slice(0, anchorIndex + 1),
        tab,
        ...items.slice(anchorIndex + 1),
    ];
}

/**
 * ``Waiting List (N)`` — the sidebar label once the pending-request count is
 * known, so a god sees whether anyone is waiting without opening the tab
 * (the same string pattern ``Settings`` uses for ``Model Providers (N)``;
 * Spark's tabs have no badge slot). Anything but a non-negative integer —
 * ``null`` while loading, after a failed fetch, or for a non-god — leaves the
 * bare label, so the tab never flashes a stale or made-up number.
 */
export function waitingListTabLabel(pendingCount) {
    if (Number.isInteger(pendingCount) && pendingCount >= 0) {
        return `${WAITING_LIST_TAB.label} (${pendingCount})`;
    }
    return WAITING_LIST_TAB.label;
}

/**
 * Return the visible tab list.
 *
 * ``tabItems`` is the already role-filtered list (api-keys / users filters
 * applied by ``Settings``). The billing tab is inserted right after the
 * ``users`` tab only when all three hold: the platform runs as SaaS,
 * billing is enabled on the backend, and the current user is an Admin.
 * Otherwise the list is returned unchanged, so self-hosted installs never
 * see (or lazy-load) the Stripe-backed section. The credit-usage tab follows
 * billing under the same gate.
 *
 * The waiting-list tab is inserted right after ``users`` only for ``isGod``
 * callers (platform role ``god``, or any user when auth is disabled —
 * mirroring the backend ``require_god`` bypass) and only on SaaS: the
 * waiting list does not exist on self-hosted installs, where strangers are
 * invited by a workspace admin instead (the backend answers 404 there). Its
 * label carries the pending count (``waitingListPendingCount``) when one is
 * known — see ``waitingListTabLabel``.
 *
 * The workspace tab is prepended (always first, on every cloud) for every
 * member, whatever their role: it is the default landing tab. Admins and
 * gods get the editor there; Editors and Viewers a read-only summary.
 *
 * The ``providers`` tab is dropped when ``canManageProviders`` is false, and
 * the four ``PLATFORM_SETTINGS_TAB_IDS`` when ``canManagePlatformSettings`` is
 * false (both: SaaS non-god — the keys and the deploy targets are
 * SpecificAI's, see ``features/auth/providerAccess``). The backend returns
 * 403 on the matching write routes and redacts the read, so this only
 * decides what to render.
 */
export function getVisibleSettingsTabs({
    tabItems,
    isAdmin,
    isSaas,
    billingEnabled,
    isGod = false,
    canManageProviders = true,
    canManagePlatformSettings = true,
    waitingListPendingCount = null,
}) {
    const allItems = Array.isArray(tabItems) ? tabItems : [];
    const items = allItems.filter((item) => {
        if (item.id === PROVIDERS_TAB_ID) return canManageProviders !== false;
        if (PLATFORM_SETTINGS_TAB_IDS.includes(item.id)) return canManagePlatformSettings !== false;
        return true;
    });
    const showBilling = isSaas === true && billingEnabled === true && isAdmin === true;
    const showWaitingList = isGod === true && isSaas === true;
    const withBilling = withGatedTab(
        withGatedTab(items, BILLING_TAB, BILLING_ANCHOR_TAB_IDS, showBilling),
        CREDIT_USAGE_TAB,
        [BILLING_TAB.id],
        showBilling,
    );
    const waitingListTab = { ...WAITING_LIST_TAB, label: waitingListTabLabel(waitingListPendingCount) };
    const withWaitingList = withGatedTab(withBilling, waitingListTab, WAITING_LIST_ANCHOR_TAB_IDS, showWaitingList);
    const withoutWorkspace = withWaitingList.filter((item) => item.id !== WORKSPACE_TAB.id);
    return [WORKSPACE_TAB, ...withoutWorkspace];
}
